import React, { useMemo } from 'react';
import { Box, Grid, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { EditType } from '../types';

interface DataSource {
  name: string;
  type: string;
  technologies?: string[];
}

interface DataSourcesPanelProps {
  displayData: any;
  llmResponseData: any[];
  llmResponseLoading: boolean;
  isLoadingImplementation: boolean;
  isEditing: boolean;
  onEdit: (type: EditType, item?: any) => void;
}

const DataSourcesPanel: React.FC<DataSourcesPanelProps> = ({
  displayData,
  llmResponseData,
  llmResponseLoading,
  isLoadingImplementation,
  isEditing,
  onEdit,
}) => {
  const { internalSources, externalSources } = useMemo(() => {
    let descriptionAssets: { data_asset: string; technology: string[] }[] = [];
    try {
      const aiSolution = displayData?.agent?.ai_solution;
      descriptionAssets = llmResponseData && Array.isArray(aiSolution) ? aiSolution : [];
    } catch {
      descriptionAssets = [];
    }

    const transformSources = (items: string[] | undefined, type: string): DataSource[] =>
      (items || []).map((name) => {
        const matched = descriptionAssets.find(
          (a) => a.data_asset?.toLowerCase() === name.toLowerCase()
        );
        return {
          name,
          type,
          technologies: Array.isArray(matched?.technology) ? matched.technology : [],
        };
      });

    return {
      internalSources: [
        ...transformSources(displayData?.data_sources?.internal?.structured, 'Structured'),
        ...transformSources(displayData?.data_sources?.internal?.unstructured, 'Unstructured'),
      ],
      externalSources: [
        ...transformSources(displayData?.data_sources?.external?.structured, 'Structured'),
        ...transformSources(displayData?.data_sources?.external?.unstructured, 'Unstructured'),
      ],
    };
  }, [displayData, llmResponseData]);

  const renderChip = (label: string) => (
    <Typography
      key={label}
      variant="caption"
      sx={{
        borderRadius: '12px',
        px: 1.5,
        py: 0.5,
        borderWidth: '0.5px',
        borderStyle: 'solid',
        borderColor: '#DDE1E6',
        fontSize: '12px',
        lineHeight: '14px',
        color: 'text.primary',
        textAlign: 'center',
        ml: 1,
      }}
    >
      {label}
    </Typography>
  );

  const renderSourceItem = (item: DataSource, sourceCategory: 'internal' | 'external') => (
    <Box key={item.name} sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <img
          src="/images/db.svg"
          alt=""
          style={{ width: 16, height: 16, padding: 4, marginTop: 2 }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mr: 1 }}>
          {item.name}
        </Typography>
      </Box>
      {renderChip(item.type)}
      {llmResponseLoading ? (
        <Typography
          sx={{
            borderRadius: '12px',
            px: 1,
            borderWidth: '0.5px',
            borderStyle: 'solid',
            borderColor: '#DDE1E6',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 1,
            height: '22px',
          }}
        >
          <Skeleton variant="rectangular" height={8} width={70} sx={{ borderRadius: '12px' }} />
        </Typography>
      ) : (
        <Typography sx={{ mt: '4px' }}>
          {item.technologies?.map((tech) => renderChip(tech))}
        </Typography>
      )}
      {isEditing && (
        <DriveFileRenameOutlineIcon
          sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
          onClick={() =>
            onEdit('data_requirements', {
              name: item.name,
              originalName: item.name,
              type: item.type,
              sourceCategory,
              technologies: item.technologies,
            })
          }
        />
      )}
    </Box>
  );

  const hasData = internalSources.length > 0 || externalSources.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Panel header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: '3rem',
          bgcolor: '#1A55F9',
          color: 'white',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          fontWeight: 'bold',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src="/images/data_sources.svg" style={{ marginRight: '8px' }} alt="" />
          <Typography>Data Sources</Typography>
        </Box>
        {isEditing && (
          <IconButton size="small" onClick={() => onEdit('add_data_requirements')}>
            <AddBoxOutlinedIcon sx={{ color: 'white' }} />
          </IconButton>
        )}
      </Box>

      {/* Panel body */}
      <Stack
        spacing={2}
        alignItems="baseline"
        justifyContent="flex-start"
        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, border: '1px solid #ccc' }}
      >
        {hasData ? (
          <Grid container direction="column">
            {internalSources.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
                  Internal Data Sources
                </Typography>
                {internalSources.map((item) => renderSourceItem(item, 'internal'))}
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
                External Data Sources
              </Typography>
              {externalSources.length > 0 ? (
                externalSources.map((item) => renderSourceItem(item, 'external'))
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '14px' }}>
                  None
                </Typography>
              )}
            </Box>
          </Grid>
        ) : isLoadingImplementation ? (
          <Grid container spacing={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ height: '1.5em', bgcolor: 'grey.200', borderRadius: 1 }} />
              ))}
            </Box>
          </Grid>
        ) : (
          <Typography>No data requirements available</Typography>
        )}
      </Stack>
    </Box>
  );
};

export default DataSourcesPanel;
